// /parts/boms — every product with a parts list (drone models, chargers, any
// finished good), and how many the shelf can build today. Both instances; each
// database holds its own products.
//
// A row opens /parts/boms/:key (BomPage). The table is the shared DataTable
// with module-scope columns; the admin switch reaches its cell through the
// table `meta`, so the columns array never rebuilds.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { buttonClass } from '../components/ui/Button';
import { Link, useHistory } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import ItemPicker from '../components/ItemPicker';
import { Field } from '../components/ui/Field';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/ui/EmptyState';
import { Popover } from '../components/ui/Popover';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { Switch } from '../components/ui/Switch';
import { Tag } from '../components/ui/Tag';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { DataTable } from '../components/table/DataTable';
import {
  useBomsApi,
  bomErrorMessage,
  bomPath,
  productCode,
  productName,
  variantLabel,
  type Capacity,
  type ProductSummary,
} from './bomsApi';

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

interface DashboardMeta {
  onToggle: (id: number, next: boolean) => void;
}

const getRowId = (p: ProductSummary) => String(p.id);

const lowestBuilds = (p: ProductSummary): number | undefined =>
  p.capacity.length ? Math.min(...p.capacity.map((c) => c.builds)) : undefined;

const OnTheShelf: React.FC<{ c: Capacity }> = ({ c }) =>
  c.raw_builds !== c.builds ? (
    <div className="text-[11px] text-on-surface-variant tabular-nums whitespace-nowrap">
      of {c.raw_builds} on the shelf
    </div>
  ) : null;

const BuildsToday: React.FC<{ capacity: Capacity[] }> = ({ capacity }) => {
  if (capacity.length === 0) {
    return <span className="text-on-surface-variant">—</span>;
  }
  if (capacity.length === 1) {
    const only = capacity[0];
    return (
      <div className="min-w-0">
        <span className="font-semibold tabular-nums text-on-surface">{only.builds}</span>
        <OnTheShelf c={only} />
        {only.limited_by && (
          <div className="max-w-[16rem] truncate text-[11px] text-on-surface-variant">
            limited by {only.limited_by.name}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap justify-end gap-x-4 gap-y-1">
      {capacity.map((c) => (
        <div key={c.variant ?? 'all'} className="min-w-0">
          <div className="text-[11px] text-on-surface-variant whitespace-nowrap">
            {variantLabel(c.variant)}
          </div>
          <div className="font-semibold tabular-nums text-on-surface">{c.builds}</div>
          <OnTheShelf c={c} />
        </div>
      ))}
    </div>
  );
};

const BASE_COLUMNS: ColumnDef<ProductSummary, any>[] = [
  {
    id: 'product',
    header: 'Product',
    accessorFn: (p: ProductSummary) => productCode(p),
    meta: { cellClass: 'max-w-[22rem]' },
    cell: ({ row }) => {
      const p = row.original;
      const code = productCode(p);
      const name = productName(p);
      return (
        <div className={`min-w-0 ${p.is_active ? '' : 'text-on-surface-variant'}`}>
          <div className="flex items-baseline gap-2">
            <Link
              to={bomPath(p.product_key)}
              onClick={(e) => e.stopPropagation()}
              className="font-bold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
            >
              {code}
            </Link>
            {!p.is_active && (
              <Tag size="sm" className="shrink-0">Retired</Tag>
            )}
          </div>
          {name !== code && <div className="truncate text-xs text-on-surface-variant">{name}</div>}
        </div>
      );
    },
  },
  {
    id: 'drones',
    header: 'Drones',
    accessorFn: (p: ProductSummary) => (p.model_code ? p.drone_count : undefined),
    meta: { align: 'right', numeric: true },
    cell: ({ row }) =>
      row.original.model_code ? (
        <span className={row.original.is_active ? '' : 'text-on-surface-variant'}>
          {row.original.drone_count}
        </span>
      ) : (
        <span className="text-on-surface-variant">—</span>
      ),
  },
  {
    id: 'parts_list',
    header: 'Parts list',
    accessorFn: (p: ProductSummary) => (p.bom_id ? p.line_count : undefined),
    cell: ({ row }) => {
      const p = row.original;
      if (!p.bom_id) {
        return <span className="text-on-surface-variant">No parts list yet</span>;
      }
      return (
        <div className={`min-w-0 ${p.is_active ? '' : 'text-on-surface-variant'}`}>
          <div className="truncate">{p.bom_label || 'Current list'}</div>
          <div className="text-xs text-on-surface-variant tabular-nums">
            {p.line_count} {p.line_count === 1 ? 'line' : 'lines'}
          </div>
        </div>
      );
    },
  },
  {
    id: 'builds',
    header: 'Builds today',
    accessorFn: (p: ProductSummary) => lowestBuilds(p),
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => <BuildsToday capacity={row.original.capacity} />,
  },
];

// Admins only: a second module-scope array rather than a column that hides,
// so a non-admin's table has no trace of the control.
const ADMIN_COLUMNS: ColumnDef<ProductSummary, any>[] = [
  ...BASE_COLUMNS,
  {
    id: 'dashboard',
    header: 'Show on dashboard',
    accessorFn: (p: ProductSummary) => (p.on_dashboard ? 1 : 0),
    meta: { align: 'right' },
    cell: ({ row, table }) => {
      const p = row.original;
      const { onToggle } = table.options.meta as DashboardMeta;
      return (
        // The label stops the click so the row does not open. A real checkbox
        // rather than a button: the table's Enter-opens-row key skips inputs.
        <Switch
          checked={p.on_dashboard}
          onChange={(e) => onToggle(p.id, e.target.checked)}
          aria-label={`Show ${productCode(p)} on dashboard`}
          labelProps={{ onClick: (e) => e.stopPropagation() }}
        />
      );
    },
  },
];

/** Admin only. Reads as a sentence; a click turns the figure into a field. */
const MarginEditor: React.FC<{
  percent: number;
  onSave: (percent: number) => Promise<void>;
}> = ({ percent, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set on the first finish: Enter or Escape closes the field, and the blur that
  // closing causes must not save a second time.
  const done = useRef(false);

  const finish = async (save: boolean) => {
    // A keystroke during the save re-arms `done`; the blur that follows must not send a second PUT.
    if (done.current || busy) return;
    done.current = true;
    const text = draft.trim();
    if (!save || text === '' || Number(text) === percent) {
      setEditing(false);
      setError(null);
      return;
    }
    setBusy(true);
    try {
      await onSave(Number(text));
      setEditing(false);
      setError(null);
    } catch (err) {
      setError(bomErrorMessage(err));
      done.current = false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col">
      <FadeIn key={editing ? 'editing' : 'reading'}>
      {editing ? (
        <span className="inline-flex items-center gap-2">
          <label htmlFor="bom-failure-margin" className="text-on-surface">
            Failure margin
          </label>
          <span className="inline-flex items-center gap-1">
            <Field
              size="sm"
              error={!!error}
              id="bom-failure-margin"
              autoFocus
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              max="99.99"
              value={draft}
              aria-busy={busy}
              aria-invalid={!!error}
              aria-describedby={error ? 'bom-failure-margin-error' : undefined}
              onChange={(e) => {
                setDraft(e.target.value);
                done.current = false;
              }}
              onBlur={() => finish(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  finish(true);
                } else if (e.key === 'Escape') {
                  e.stopPropagation();
                  finish(false);
                }
              }}
              className={`!w-20 text-right ${busy ? 'motion-safe:animate-pulse' : ''}`}
            />
            <span aria-hidden="true">%</span>
          </span>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => {
            done.current = false;
            setDraft(String(percent));
            setError(null);
            setEditing(true);
          }}
          className={`group inline-flex items-center gap-2 rounded px-1 py-0.5 -mx-1 hover:bg-surface-container-high ${FOCUS}`}
        >
          <span>
            Failure margin{' '}
            <span className="font-semibold tabular-nums text-on-surface">{percent}%</span>
          </span>
          <span className="text-xs group-hover:text-on-surface">Change</span>
        </button>
      )}
      </FadeIn>
      <Reveal show={!!error}>
        <span id="bom-failure-margin-error">
          <Alert variant="text" className="text-xs">{error}</Alert>
        </span>
      </Reveal>
    </span>
  );
};

/** The header action. A popover under its own button: the pick lands where the pointer is. */
const ProductStarter: React.FC<{ onPick: (key: string) => void }> = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLElement | null>(null);

  return (
    <div ref={wrap} className="relative">
      <SecondaryAction
        onClick={() => {
          trigger.current = wrap.current?.querySelector('button') ?? null;
          setOpen((o) => !o);
        }}
      >
        Start a parts list
      </SecondaryAction>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={wrap}
        returnTo={trigger}
        label="Start a parts list"
        align="end"
        width="md"
        hideClose
      >
        <p className="mb-3 text-sm text-on-surface-variant">
          Pick the finished product. Its page opens, ready for its first list.
        </p>
        <ItemPicker
          value={null}
          onSelect={(row) => {
            if (!row) return;
            setOpen(false);
            onPick(row.model_code || String(row.id));
          }}
          extraParams={{ material_type: 'finished_good' }}
          placeholder="Find a finished product"
          label="Finished product"
          autoFocus
        />
      </Popover>
    </div>
  );
};

const ProductBomsPage: React.FC = () => {
  const boms = useBomsApi();
  const history = useHistory();
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [margin, setMargin] = useState<number | null>(null);
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const isAdmin = !!(user?.isadmin || user?.can_admin);
  const canWrite = !!(user?.isadmin || user?.can_admin || user?.can_manufacturing);
  // true once somebody has picked; the default selection has no list to go back from.
  const [custom, setCustom] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, m] = await Promise.all([
        boms.listProducts(),
        boms.getFailureMargin().catch(() => null),
      ]);
      setProducts(list || []);
      setMargin(m ? m.percent : null);
      if (isAdmin) {
        const picked = await boms.getDashboardProducts().catch(() => null);
        setCustom(picked?.ids != null);
      }
    } catch (err) {
      setError(bomErrorMessage(err, 'The products did not load.'));
    } finally {
      setLoading(false);
    }
    // isAdmin: a deep-link refresh can mount before the user loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // One PUT at a time: each sends the whole list, so a second flip while the
  // first is in flight could land out of order or revert the wrong switch.
  const savingRef = useRef(false);

  const toggleDashboard = useCallback(
    async (id: number, next: boolean) => {
      if (savingRef.current) return;
      savingRef.current = true;
      const before = products;
      const after = products.map((p) => (p.id === id ? { ...p, on_dashboard: next } : p));
      setProducts(after);
      try {
        await boms.setDashboardProducts(after.filter((p) => p.on_dashboard).map((p) => p.id));
        setCustom(true);
      } catch (err) {
        setProducts(before);
        toast.error(bomErrorMessage(err, 'The dashboard did not change. Try again.'));
      } finally {
        savingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products],
  );

  const backToDefault = async () => {
    try {
      await boms.setDashboardProducts(null);
      setCustom(false);
      setProducts((await boms.listProducts()) || []);
    } catch (err) {
      toast.error(bomErrorMessage(err, 'The dashboard did not change. Try again.'));
    }
  };

  // Throws on a refused save so the editor can say why beside the field.
  const saveMargin = async (percent: number) => {
    const saved = await boms.setFailureMargin(percent);
    setMargin(saved.percent);
    try {
      setProducts((await boms.listProducts()) || []);
    } catch (err) {
      toast.error(bomErrorMessage(err, 'The margin saved, but the figures did not refresh. Reload the page.'));
    }
  };

  const tableMeta = useMemo<DashboardMeta>(() => ({ onToggle: toggleDashboard }), [toggleDashboard]);

  const open = (p: ProductSummary) => history.push(bomPath(p.product_key));

  return (
    <PageLayout width="wide"
      kind="workspace"
      className="space-y-5 pt-4"
      header={
      <PageHeader
        title="Product BOMs"
        helpTitle="About product BOMs"
        lede="Each product's parts list, and how many the shelf can build today."
        help={
          <>
            <p>
              A product is a drone model, such as F021 or D002, or any other finished good
              someone has started a list for, such as a charger. Its parts list says what goes
              into one unit and how many of each.
            </p>
            <p className="text-on-surface font-medium">Builds today</p>
            <p>
              How many good units the shelf can build right now, after the failure margin. The
              number before the margin sits under it. The part that runs out first is named. A
              model with a pyro build and a standard build shows one figure for each.
            </p>
            <p>
              The failure margin
              {margin !== null && <span className="tabular-nums"> ({margin}% now)</span>} allows for
              parts that fail at test.
            </p>
            <p className="text-on-surface font-medium">Where products come from</p>
            <p>
              A drone model appears when its first drone is minted. Any other finished product
              appears once its list is started with Start a parts list here.
            </p>
          </>
        }
        actions={canWrite ? <ProductStarter onPick={(key) => history.push(bomPath(key))} /> : undefined}
      />
      }
    >

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-on-surface-variant">
          <p>The dashboard's build capacity card shows the products switched on here.</p>
          <Appear show={!!custom} as="div">
            <button
              type="button"
              onClick={backToDefault}
              className={buttonClass('quiet', 'sm')}
            >
              Back to default
            </button>
          </Appear>
          {margin !== null && (
            <div className="sm:ml-auto">
              <MarginEditor percent={margin} onSave={saveMargin} />
            </div>
          )}
        </div>
      )}

      <FadeIn key={error ? 'error' : 'table'}>
      {error ? (
        <Alert onRetry={load}>{error}</Alert>
      ) : (
        <DataTable<ProductSummary>
          data={products}
          columns={isAdmin ? ADMIN_COLUMNS : BASE_COLUMNS}
          meta={tableMeta}
          getRowId={getRowId}
          sortable
          initialSorting={[{ id: 'product', desc: false }]}
          rowKeyboard={{ onOpen: open }}
          rowProps={(p) => ({ onClick: () => open(p), className: 'cursor-pointer' })}
          strip={
            <span className="text-xs text-on-surface-variant tabular-nums">
              {products.length} {products.length === 1 ? 'product' : 'products'}
            </span>
          }
          loading={loading}
          loadingContent={
            <SkeletonGroup label="Loading products" className="space-y-2 py-8">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </SkeletonGroup>
          }
          empty={
            <EmptyState variant="area" title="No products with a parts list yet.">
              A drone model appears when its first drone is minted. Start a list for any other
              finished product with Start a parts list.
            </EmptyState>
          }
        />
      )}
      </FadeIn>
    </PageLayout>
  );
};

export default ProductBomsPage;
